import { z } from 'zod'

import { schemas } from '@showrunner/scrapi'

const {
  headerPrefs,
  headerOrFooterPrefs,
  headerOrFooterSlots,
  staticSlotContentType,
  slotConfig,
} = schemas.scriptFormats
export { headerPrefs, headerOrFooterPrefs, staticSlotContentType }

const slotPosition = headerOrFooterSlots.keyof()

const CSS_NUMBER_RE = /[+-]?([0-9]*[.])?[0-9]+/
type CssSizeUnit = 'px' | 'in'
const cssSize = z.custom<`${number}${CssSizeUnit}`>((val) => {
  return typeof val === 'string' ? CSS_NUMBER_RE.test(val) : false
})

export const pageMarginSizes = z.object({
  top: cssSize,
  left: cssSize,
  right: cssSize,
  bottom: cssSize,
})

const pageSize = z.object({
  height: cssSize,
  width: cssSize,
})

const orientation = z.enum(['portrait', 'landscape'])

const pageLayout = z.object({
  margins: pageMarginSizes,
  orientation: orientation,
  size: pageSize,
})

export const scriptRevisionPreferences = z.object({
  revisionRange: z.enum(['entire', 'revised', 'set']).catch('entire'),
  revisionShowPageColors: z.boolean().catch(false),
})

export const scriptPrintPreferences = z.object({
  columns: z.boolean().catch(false),
  monochrome: z.boolean().catch(false),
  twoColDialogueSpacing: z
    .union([z.literal(1), z.literal(1.5), z.literal(2)])
    .catch(1),
  includeTitlePages: z.boolean().catch(false),
  headers: headerPrefs,
  footers: headerOrFooterPrefs,
})

export const rundownPrintPrefs = z.object({
  orientation: orientation.catch('portrait'),
  pageSize: z.enum(['letter', 'a4', 'legal', 'ledger']).catch('letter'),
  monochrome: z.boolean().catch(false),
  rowDensity: z.enum(['compact', 'standard', 'loose']).catch('standard'),
})

export type StaticSlotContentType = z.infer<typeof staticSlotContentType>
export type SlotPosition = z.infer<typeof slotPosition>
export type SlotConfig = z.infer<typeof slotConfig>
export type PageMarginSizes = z.infer<typeof pageMarginSizes>
export type PageSize = z.infer<typeof pageSize>
export type PageLayout = z.infer<typeof pageLayout>
export type HeaderOrFooterPrefs = z.infer<typeof headerOrFooterPrefs>
export type ScriptRevisionPreferences = z.infer<
  typeof scriptRevisionPreferences
>
export type ScriptPrintPreferences = z.infer<typeof scriptPrintPreferences>
export type RundownPrintPrefs = z.infer<typeof rundownPrintPrefs>

export const DEFAULT_SCRIPT_REVISION_PREFS: ScriptRevisionPreferences = {
  revisionRange: 'entire',
  revisionShowPageColors: false,
}

export const DEFAULT_SCRIPT_PREFS: ScriptPrintPreferences = {
  monochrome: false,
  columns: false,
  twoColDialogueSpacing: 1,
  includeTitlePages: true,
  headers: {
    enabled: true,
    showOnFirstPage: false,
    slots: {
      right: {
        slotType: 'static',
        contentType: 'page-number',
      },
    },
  },
  footers: {
    enabled: true,
    slots: {},
  },
}
